/**
 * Checklist History Component
 *
 * View historical checklist runs with filtering.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useOsNavigate } from "@/components/ui/os-layout";
import { OpsSubpageHeadingClassic } from "@/components/ops/OpsSubpageHeadingClassic";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleNotch,
  faFilter,
  faChevronDown,
  faChevronUp,
  faClock,
  faUser,
  faCheck,
  faTimes,
  faEye,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { Badge } from "@/components/ui/badge/Badge";
import { useNotification } from "@/context/NotificationContext";
import {
  getHistory,
  getChecklists,
  getRunStatusColor,
  getRunStatusLabel,
  getStepStatusBgClass,
  type ChecklistRun,
  type Checklist,
  type HistoryResponse,
  type RunStatus,
  OPS_TEAM_MEMBERS,
} from "@/services/api/opsChecklists";

interface HistoryRowProps {
  run: ChecklistRun;
  isExpanded: boolean;
  onToggle: () => void;
}

function HistoryRow({ run, isExpanded, onToggle }: HistoryRowProps) {
  const osNavigate = useOsNavigate();

  const statusCounts = {
    green: run.steps.filter((s) => s.status === "green").length,
    amber: run.steps.filter((s) => s.status === "amber").length,
    red: run.steps.filter((s) => s.status === "red").length,
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      {/* Main row */}
      <div
        className="flex items-center gap-4 p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50"
        onClick={onToggle}
      >
        <FontAwesomeIcon
          icon={isExpanded ? faChevronUp : faChevronDown}
          className="text-slate-400 w-4"
        />

        <div className="flex-1 min-w-0">
          <div className="font-medium text-slate-900 dark:text-white">
            {run.checklist_name}
          </div>
          <div className="text-sm text-slate-500 dark:text-slate-400">
            {run.date}
          </div>
        </div>

        {/* Status dots */}
        <div className="flex items-center gap-1">
          {run.steps.map((step, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full ${getStepStatusBgClass(step.status)}`}
            />
          ))}
        </div>

        {/* Users */}
        <div className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1">
          <FontAwesomeIcon icon={faUser} className="text-xs" />
          {run.primary_user}
          {run.second_checker && (
            <span className="text-slate-400"> / {run.second_checker}</span>
          )}
        </div>

        <Badge color={getRunStatusColor(run.status)}>
          {getRunStatusLabel(run.status)}
        </Badge>

        <button
          onClick={(e) => {
            e.stopPropagation();
            if (run.status === "awaiting_review") {
              osNavigate(
                `/ops/checklists/review/${run.run_id}`,
                `Review: ${run.checklist_name}`,
              );
            } else {
              osNavigate(`/ops/checklists/run/${run.run_id}`, run.checklist_name);
            }
          }}
          className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 dark:hover:text-indigo-400 rounded-lg transition-colors"
        >
          <FontAwesomeIcon icon={faEye} />
        </button>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="border-t border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-800/50">
          {/* Summary stats */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="text-center">
              <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                {statusCounts.green}
              </div>
              <div className="text-xs text-slate-500">Green</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-semibold text-amber-600 dark:text-amber-400">
                {statusCounts.amber}
              </div>
              <div className="text-xs text-slate-500">Amber</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-semibold text-red-600 dark:text-red-400">
                {statusCounts.red}
              </div>
              <div className="text-xs text-slate-500">Red</div>
            </div>
          </div>

          {/* Timeline */}
          <div className="space-y-2 text-sm">
            {run.started_at && (
              <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                <FontAwesomeIcon icon={faClock} className="text-xs w-4" />
                Started: {new Date(run.started_at).toLocaleString()}
              </div>
            )}
            {run.completed_at && (
              <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                <FontAwesomeIcon icon={faCheck} className="text-xs w-4" />
                Completed: {new Date(run.completed_at).toLocaleString()}
              </div>
            )}
            {run.reviewed_at && (
              <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                <FontAwesomeIcon
                  icon={run.status === "approved" ? faCheck : faTimes}
                  className="text-xs w-4"
                />
                Reviewed: {new Date(run.reviewed_at).toLocaleString()}
                {run.review_notes && (
                  <span className="text-slate-500">- {run.review_notes}</span>
                )}
              </div>
            )}
          </div>

          {/* Step details */}
          <div className="mt-4 space-y-2">
            {run.steps.map((step, i) => (
              <div
                key={i}
                className="flex items-start gap-2 text-sm bg-white dark:bg-slate-800 rounded-lg p-2"
              >
                <div
                  className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${getStepStatusBgClass(step.status)}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-slate-700 dark:text-slate-300 line-clamp-1">
                    {step.notes || <em className="text-slate-400">No notes</em>}
                  </div>
                  {step.status === "red" && step.assigned_to && (
                    <div className="text-xs text-red-600 dark:text-red-400 mt-0.5">
                      Assigned to {step.assigned_to}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function ChecklistHistory() {
  const { addNotification } = useNotification();

  const [history, setHistory] = useState<HistoryResponse | null>(null);
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);

  // Filters
  const [filterChecklistId, setFilterChecklistId] = useState<string>("");
  const [filterUser, setFilterUser] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<RunStatus | "">("");
  const [filterDateFrom, setFilterDateFrom] = useState<string>("");
  const [filterDateTo, setFilterDateTo] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [historyData, checklistsData] = await Promise.all([
        getHistory({
          checklist_id: filterChecklistId || undefined,
          user_name: filterUser || undefined,
          status: (filterStatus as RunStatus) || undefined,
          date_from: filterDateFrom || undefined,
          date_to: filterDateTo || undefined,
          limit: 100,
        }),
        getChecklists(true),
      ]);
      setHistory(historyData);
      setChecklists(checklistsData);
    } catch (error) {
      console.error("Failed to load history:", error);
      addNotification({
        type: "error",
        message: "Failed to load history",
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    filterChecklistId,
    filterUser,
    filterStatus,
    filterDateFrom,
    filterDateTo,
    addNotification,
  ]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const clearFilters = () => {
    setFilterChecklistId("");
    setFilterUser("");
    setFilterStatus("");
    setFilterDateFrom("");
    setFilterDateTo("");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FontAwesomeIcon
          icon={faCircleNotch}
          className="text-2xl text-indigo-500 animate-spin"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-shrink-0 gap-4">
        <OpsSubpageHeadingClassic
          title="Checklist History"
          titleClassName="text-lg font-bold text-slate-900 dark:text-white"
          backTo="/ops/checklists"
          hubWindowTitle="Ops Checklists"
          useOsLink
          linkClassName="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          subtitle={
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {history?.total || 0} runs
            </p>
          }
        />

        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
            showFilters
              ? "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300"
              : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
          }`}
        >
          <FontAwesomeIcon icon={faFilter} />
          Filters
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 mb-6 flex-shrink-0">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                Checklist
              </label>
              <select
                value={filterChecklistId}
                onChange={(e) => setFilterChecklistId(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              >
                <option value="">All</option>
                {checklists.map((cl) => (
                  <option key={cl.checklist_id} value={cl.checklist_id}>
                    {cl.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                User
              </label>
              <select
                value={filterUser}
                onChange={(e) => setFilterUser(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              >
                <option value="">All</option>
                {OPS_TEAM_MEMBERS.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as RunStatus | "")}
                className="w-full px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              >
                <option value="">All</option>
                <option value="in_progress">In Progress</option>
                <option value="awaiting_review">Awaiting Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                From Date
              </label>
              <input
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                To Date
              </label>
              <input
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              />
            </div>
          </div>

          <div className="mt-3 flex justify-end">
            <button
              onClick={clearFilters}
              className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
            >
              Clear filters
            </button>
          </div>
        </div>
      )}

      {/* History list */}
      <div className="flex-1 min-h-0 overflow-auto space-y-3">
        {history?.runs.length === 0 ? (
          <div className="text-center py-12 text-slate-500 dark:text-slate-400">
            No runs found matching your filters.
          </div>
        ) : (
          history?.runs.map((run) => (
            <HistoryRow
              key={run.run_id}
              run={run}
              isExpanded={expandedRunId === run.run_id}
              onToggle={() =>
                setExpandedRunId(expandedRunId === run.run_id ? null : run.run_id)
              }
            />
          ))
        )}
      </div>
    </div>
  );
}
